import { useContext, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import PageContext from '@/Providers/PageContextProvider/PageContext';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';

import { wechatCopy, wechatNotice } from './copy';
import { QRSession } from './index';
import styles from './index.module.scss';
import { getWechatBindingPresentation } from './presentation';
import { readWechatNavigation } from './protocol';
import { currentWechatConnector } from './selection';

/** Keeps the original forced-account/MFA form until Native asks for its QR. */
const BindingContinuation = ({ fallback }: { readonly fallback: ReactNode }) => {
  const { experienceSettings, platform } = useContext(PageContext);
  const { state } = useLocation();
  const { i18n } = useTranslation();
  const navigate = useNavigateWithPreservedSearchParams();
  const presentation = getWechatBindingPresentation();
  const connector = currentWechatConnector(experienceSettings?.socialConnectors ?? []);
  const hint = presentation && connector ? readWechatNavigation(state) : undefined;
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const copy = wechatCopy(locale);
  const other = () => {
    navigate('/sign-in', { replace: true, state: null });
  };
  if (connector && hint?.step === 'binding-qr') {
    return (
      <QRSession
        connectorId={connector.id}
        flow="binding"
        isEmbedded={false}
        isMobile={platform === 'mobile'}
        locale={locale}
        onOtherMethod={other}
      />
    );
  }
  if (connector && hint?.step === 'error') {
    return (
      <section className={styles.panel}>
        <h1>{copy.bindTitle}</h1>
        <p className={styles.error} role="alert">
          {wechatNotice(hint.notice ?? 'unavailable', locale)}
        </p>
        <button className={styles.secondary} type="button" onClick={other}>
          {copy.other}
        </button>
      </section>
    );
  }
  return fallback;
};
export default BindingContinuation;
